import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoiceApi, useInvoiceListApi } from '@hero/api-hooks';
import type { Invoice, Id } from '@hero/schema';
import type { InvoiceGetCurrentUserAPI } from '@hero/api';
import { ReceiptIcon } from '@hero/web-icons';
import { SelectAsync, type SelectionValue, type SelectAsyncMode } from '../../../app/Select';
import { Price } from '../../../app/Price';

/** Invoice selection value: `SelectionValue<Invoice>`. */
export type InvoiceSelectorValue = SelectionValue<Invoice>;

/**
 * Subset of `InvoiceGetCurrentUserAPI['req']` we accept as a
 * pre-filter. Pagination / search are owned by the underlying
 * `SelectAsync` (it drives infinite scroll + debounced search), so
 * we omit `limit`/`skip`/`search` from the prop surface — passing
 * them would just be ignored.
 */
export type InvoiceSelectorQuery = Omit<
  InvoiceGetCurrentUserAPI['req'],
  'limit' | 'skip' | 'search'
>;

export interface InvoiceSelectorProps {
  /**
   * Currently selected invoice(s). Accepts any of the shapes the
   * underlying `SelectAsync` supports — an `Id`, an array of `Id`s,
   * a full `Invoice`, etc. Pass the stored `Invoice._id` (hex
   * string) in the common case; the selector internally fetches
   * the full doc via `useInvoiceApi` so the trigger always shows a
   * proper buyer-name / ref / amount row instead of the raw id.
   */
  selectedInvoices?: InvoiceSelectorValue;
  /** Called with the resolved `Invoice[]` (or `undefined` when cleared). */
  onInvoicesSelected: (invoices: Invoice[] | undefined) => void;
  /**
   * Pre-filter for the underlying list query. Lets the parent
   * scope the selector — e.g. `financeableOnly: true` for the
   * financing apply form, or `clientId` to narrow to a specific
   * counterparty. The most common combination is
   * `{ financeableOnly: true, clientId }`.
   */
  query?: InvoiceSelectorQuery;
  disabled?: boolean;
  placeholder?: string;
  selectMode?: SelectAsyncMode;
  /** Label above the trigger. */
  label?: string;
  required?: boolean;
  /** Inline error string. */
  error?: string;
  className?: string;
}

/**
 * Format the Hero-issued ref. `ref` is `{ main, short, custom? }`
 * on every Invoice; we prefer the carrier-friendly `short` form
 * and fall back to the last 6 chars of the id when nothing has
 * been generated (legacy invoices).
 */
function formatHeroRef(invoice: Invoice): string {
  if (invoice.ref?.short) return invoice.ref.short;
  if (invoice.ref?.main) return invoice.ref.main;
  return String(invoice._id).slice(-6);
}

/**
 * Pull the seller-typed invoice ref from `invoice.invoiceProof`.
 * Filled in by the seller when they reconcile against their own
 * accounting system; empty until they do. Distinct from
 * `invoice.ref` (the Hero-generated id), which is always present.
 */
function formatSellerRef(invoice: Invoice): string | null {
  const ref = invoice.invoiceProof?.refNumber?.trim();
  return ref ? ref : null;
}

/**
 * Format a date in the user's current locale as a short
 * month/day pair — e.g. "Jun 18" in English or "‏18 يونيو" in
 * Arabic. Used in the selector rows so the user reads "Due Jun
 * 18" instead of the raw "6/18/2026" toLocaleDateString output.
 */
function formatShortMonthDay(date: Date | string, lang: string): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(lang, {
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * Render label + amount + meta row for a single invoice option in
 * the popover. Pulled into its own component so we can keep the
 * dropdown rows visually rich (icon + dual refs + due date + price
 * chip) while the compact trigger uses the lighter
 * `getOptionDisplay` label/desc shape.
 */
function InvoiceRow({ invoice, lang }: { invoice: Invoice; lang: string }) {
  const { t } = useTranslation('m-financing');
  const buyerName = invoice.buyer?.name ?? '—';
  const heroRef = formatHeroRef(invoice);
  const sellerRef = formatSellerRef(invoice);
  const due = invoice.period?.due ? formatShortMonthDay(invoice.period.due, lang) : null;

  return (
    <div className="flex w-full items-center gap-3">
      {/*
       * Leading icon — mirrors the avatar slot used by `ClientSelector`
       * (`avatarBg` / `avatarText` in `SelectAsyncOptionRow`), but we
       * render it inline because invoices don't have an "initial"
       * letter that means anything to the user. A receipt glyph in an
       * emerald chip keeps the dropdown visually scannable.
       */}
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
        <ReceiptIcon className="size-4" />
      </span>

      <div className="flex min-w-0 flex-col items-start leading-tight">
        <span className="truncate text-sm font-medium text-gray-900">{buyerName}</span>
        <span className="truncate text-xs text-gray-500">
          {/*
           * Always show the Hero ref so the user can correlate the
           * pick against the invoice table; show the seller's own
           * accounting ref *next to it* when set so it's obvious
           * they're tied to the same record. `due` is rendered as
           * "Due Jun 18" via `formatShortMonthDay`.
           */}
          {t('invoice_selector.row.hero_ref', { ref: heroRef })}
          {sellerRef
            ? ` · ${t('invoice_selector.row.seller_ref', { ref: sellerRef })}`
            : ''}
          {due ? ` · ${t('invoice_selector.row.due', { date: due })}` : ''}
        </span>
      </div>

      <Price
        amount={invoice.amount?.remaining ?? invoice.amount?.total ?? 0}
        currency={invoice.amount?.currency ?? 'SAR'}
        size="sm"
        className="ms-auto shrink-0 font-semibold text-emerald-700"
      />
    </div>
  );
}

/**
 * `InvoiceSelector` — pick an existing `Invoice` from the
 * current user's invoice list via async-search.
 *
 * Sibling of `ClientSelector` — same `SelectAsync` chassis, same
 * "value can be an id, full doc, or array" semantics, same
 * `query` pre-filter pattern. The selector internally wires
 * `useInvoiceListApi` (paginated list with search) and
 * `useInvoiceApi` (single-doc resolver for an id passed in via
 * `value`), so consumers can store **just** the id string in their
 * form state and the trigger still shows a proper invoice row.
 *
 * Common pre-filter combinations on `query`:
 *  - `{ financeableOnly: true }` — only invoices with a payable
 *    balance, used by the financing apply form.
 *  - `{ clientId }` — narrow to a single counterparty.
 *  - `{ financeableOnly: true, clientId }` — both together.
 *
 * Selection mode defaults to `'single'` (the financing apply
 * form needs one invoice per application). Set `selectMode="multi"`
 * for batch-style usages.
 */
export const InvoiceSelector = React.memo(function InvoiceSelector({
  selectedInvoices,
  onInvoicesSelected,
  query,
  disabled = false,
  placeholder,
  selectMode = 'single',
  label,
  required,
  className,
}: InvoiceSelectorProps) {
  const { t, i18n } = useTranslation(['m-financing', 'c-main']);

  // `useInvoiceListApi` requires a defined query object. Default
  // to an empty filter so callers can omit the prop when they
  // want all current-user invoices unfiltered.
  const listQuery = useInvoiceListApi(query ?? {});
  const singleQuery = useInvoiceApi(undefined as Id | undefined);

  return (
    <div className={className}>
      <SelectAsync<Invoice>
        value={selectedInvoices}
        listQuery={listQuery}
        singleQuery={singleQuery}
        onChangeSelect={onInvoicesSelected}
        getItemId={inv => inv._id}
        placeholder={
          placeholder ?? t('m-financing:invoice_selector.placeholder')
        }
        searchPlaceholder={t('m-financing:invoice_selector.search_placeholder')}
        emptyMessage={t('m-financing:invoice_selector.empty')}
        loadingMessage={t('c-main:async_operations.loading')}
        // Inline create is not meaningful for invoices in this
        // flow — invoices are generated by orders, not typed by
        // hand. The "External invoice" tab on the apply form
        // handles the alternative path.
        allowCreateNew={false}
        allowEdit={false}
        mode={selectMode}
        disabled={disabled}
        label={label}
        required={required}
        // Drives both the trigger summary AND the dropdown row
        // fallback. Without this `SelectAsync` falls back to
        // `item.name ?? item.label ?? _id` — `Invoice` has none
        // of those, which is what was leaking the raw ObjectId
        // hex string into the trigger ("6a342e17a9fb839e7a418960").
        getOptionDisplay={invoice => {
          const heroRef = formatHeroRef(invoice);
          const sellerRef = formatSellerRef(invoice);
          const buyerName = invoice.buyer?.name ?? '—';
          const due = invoice.period?.due
            ? formatShortMonthDay(invoice.period.due, i18n.language)
            : null;
          const refLine = sellerRef ? `${heroRef} · ${sellerRef}` : heroRef;
          return {
            label: `${buyerName} · ${refLine}`,
            desc: due ? t('m-financing:invoice_selector.row.due', { date: due }) : undefined,
          };
        }}
        // Richer row inside the popover — leaves the trigger to
        // the compact label/desc shape from `getOptionDisplay`.
        renderOption={invoice => <InvoiceRow invoice={invoice} lang={i18n.language} />}
      />
    </div>
  );
});
